import { ImgCdnParameters, ImgCdnImageFormat, ImgCdnImageTransformFn } from './types';
import { ImgCdnUrlBuilder } from './img-cdn-url-builder';
import { captureException } from '@core/plugins/sentry';
import { FeatureFlagService } from '@client/monolith/legacy/types/feature-flag-service';

export class ImgCdnService {
  constructor(
    private readonly imgCdnBaseUrl: string,
    private readonly featureFlagService: FeatureFlagService
  ) {}

  public imgCdnUrl(filename: string, size: string = null, extraParameters: ImgCdnParameters = {}): string {
    try {
      const url = new ImgCdnUrlBuilder(this.imgCdnBaseUrl);
      url.setPath(filename);
      url.addParameter('dpr', this.devicePixelRatio);

      Object.entries(extraParameters).forEach(([key, value]) => {
        url.addParameter(key as keyof ImgCdnParameters, value);
      });

      if (size && !url.hasParameter('w')) {
        url.addParameter('w', size);
      }

      if (this.featureFlagService.isEnabled('img_cdn_webp')) {
        url.addParameter('force_format', ImgCdnImageFormat.WEBP);
      }

      return url.build();
    } catch (e) {
      captureException(e);
      return '';
    }
  }

  public imgCdnUrlFill(filename: string, size: string, extraParameters: ImgCdnParameters = {}): string {
    return this.imgCdnUrl(filename, size, {
      h: `${size}`,
      w: `${size}`,
      func: ImgCdnImageTransformFn.FIT,
      dpr: this.devicePixelRatio,
      ...extraParameters,
    });
  }

  public imgCdnUrlCrop(filename: string, size: string, extraParameters: ImgCdnParameters = {}): string {
    const cropGravity: ImgCdnParameters['gravity'] = this.featureFlagService.getVariant('crop_image_algo');
    const parameters: ImgCdnParameters = {
      h: `${size}`,
      w: `${size}`,
      func: cropGravity === 'off' ? ImgCdnImageTransformFn.FACE : ImgCdnImageTransformFn.CROP,
      dpr: this.devicePixelRatio,
    };
    if (cropGravity !== 'off') {
      parameters.gravity = cropGravity;
    }
    return this.imgCdnUrl(filename, size, {
      ...parameters,
      ...extraParameters,
    });
  }

  private get devicePixelRatio() {
    return `${globalThis.devicePixelRatio ?? 1}`;
  }
}
